<template>
  <div>
    <!-- Empty state (mirrors the legacy gas/elec leaf components) -->
    <div class="row" v-if="isEmpty">
      <div class="col-12">
        <div class="h-100">
          <div class="d-flex h-100 justify-content-center align-items-center">
            <img
              src="@/assets/img/tick_selector.png"
              class="img-fluid d-inline-block"
              alt="No readings"
            />
            <p v-if="!hasAnyReadings" class="d-inline-block my-auto pl-2">
              No readings have been added to date.
            </p>
            <p v-else class="d-inline-block my-auto pl-2">
              No readings match current filter.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Consolidated reading table (single shared SchemaTable for both fuels) -->
    <div
      v-else-if="showTable"
      class="table-div"
      style="overflow: auto; max-height: 300px"
    >
      <schema-table
        :schema="schema"
        :data="rows"
        @billing-toggle="onBillingToggle"
        @row-click="onRowClick"
      />
    </div>
  </div>
</template>

<script>
import SchemaTable from '@/components/common/SchemaTable.vue';

export default {
  name: 'readings-table',

  components: { SchemaTable },

  props: {
    fuel: {
      type: String,
      required: true,
      validator: (value) => value === 'gas' || value === 'elec',
    },
  },

  computed: {
    isGas() {
      return this.fuel === 'gas';
    },

    hasConverter() {
      return this.$store.getters.hasConverter;
    },

    rows() {
      return this.$store.getters.readingsTableRows(this.fuel);
    },

    schema() {
      const columns = this.$store.getters
        .readingsTableColumns(this.fuel, this.hasConverter)
        .map((column) =>
          column.component === 'BillingStatusToggle'
            ? {
                ...column,
                componentProps: {
                  ...column.componentProps,
                  billingInfoResolver: this.billingInfo,
                },
              }
            : column
        );

      const options = {
        striped: true,
        hover: false,
        small: true,
        responsive: true,
        pagination: false,
      };

      // Both fuels render the QueenBee-derived row colour (`_tableClass`) so gas and elec have
      // parity per the PO spec (CoT/CoS/Installed/Removed rows). Rows with no flag get '' (no class).
      options.tbodyTrClass = (item) => (item && item._tableClass) || '';

      return { columns, options };
    },

    totalReadings() {
      const service = this.$store.state.meters.activeService;
      return service && service.Readings ? service.Readings.length : 0;
    },

    hasAnyReadings() {
      return this.totalReadings > 0;
    },

    isEmpty() {
      return this.rows.length === 0;
    },

    contractsByServiceIdentifier() {
      return this.$store.state.meters.contractsByServiceIdentifier || [];
    },

    showTable() {
      // Gas only renders once the contract context is available (legacy guard).
      return this.isGas ? this.contractsByServiceIdentifier.length !== 0 : true;
    },

    bills() {
      return this.$store.state.finances.bills;
    },
  },

  methods: {
    billingInfo(invoice) {
      return (this.bills || []).filter((bill) => bill.InvoiceNumber === invoice);
    },

    // Toggles the DoNotBill flag on a reading. Still served by ReadingBot (QueenBee is
    // read-only for reading retrieval); mirrors the legacy mixin `isBillable`.
    onBillingToggle(payload) {
      const item = payload && payload.row;
      if (!item) return;

      const config = this.$config.honeycomb.readingsbot;

      if (this.isGas) {
        this.$http
          .put(
            'https://' + config.gas.domain + '/api/v1/readings/' + item.SharedId,
            { SharedId: item.SharedId, DoNotBill: !item.DoNotBill },
            { withCredentials: true }
          )
          .then(() => {
            this.$store.dispatch('refreshReadings');
          });
        return;
      }

      this.$http
        .put(
          'https://' + config.electricity.domain + '/api/v1/readings/' + item.Id,
          { Id: item.Id, DoNotBill: !item.DoNotBill },
          { withCredentials: true }
        )
        .then(() => {
          this.$store.dispatch('refreshReadings');
        });
    },

    onRowClick(payload) {
      const item = payload && payload.item;
      if (item) this.$store.commit('updateReadingsToggle', item);
    },
  },
};
</script>

<style lang="css">
.mexReading {
    /* Installed / Removed rows (PO spec) */
    background-color: #ecfce4 !important;
    border: solid #909090 1px;
}

.cotReading {
    /* CoT Start / CoT End rows (PO spec) */
    background-color: #fce4ec !important;
    border: solid #909090 1px;
}

.cosReading {
    /* CoS Start / CoS End rows (PO spec) */
    background-color: #e4ecfc !important;
    border: solid #909090 1px;
}

.badge-billing-info {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #495057;
    background-color: transparent;
    border: 1px solid #28a745;
    margin: 0.5em;
    min-width: 2em;
    border-radius: 35%;
}
</style>
